import { Feature, MultiPolygon, Polygon } from "geojson";

import type { MaritimeWarningDomainId } from "@/lib/maritime-domains";

import { getAreaName } from "./maritime-warnings.utils";
import { MaritimeArea } from "./maritime-areas.queries";
import { MaritimeWarning } from "./maritime-warnings.queries";

/** A maritime area with a concrete geometry, i.e. one that can be drawn on the map. */
export type DrawableMaritimeArea = MaritimeArea & { geometry: Polygon | MultiPolygon };

export type MapRegion = {
  area: DrawableMaritimeArea;
  warnings: MaritimeWarning[];
  geometry: Feature;
};

export function areaToFeature(area: DrawableMaritimeArea): Feature {
  return {
    type: "Feature",
    id: area.id,
    properties: {
      id: area.id,
      domain_id: area.domainId,
      name_nl: getAreaName(area, "nl"),
      name_en: getAreaName(area, "en"),
    },
    geometry: area.geometry,
  };
}

export function buildMapRegions(
  areas: MaritimeArea[],
  warnings: MaritimeWarning[],
  enabledDomainIds: MaritimeWarningDomainId[] = [],
): MapRegion[] {
  const domainScopedAreas =
    enabledDomainIds.length > 0
      ? areas.filter((area) => enabledDomainIds.includes(area.domainId))
      : areas;
  // Drop non-physical areas: the API returns a `null` geometry for them and
  // they cannot be drawn or measured on the map. Filtering here keeps every
  // downstream map consumer (bounds fitting, layers, markers) crash-free.
  const scopedAreas = domainScopedAreas.filter(
    (area): area is DrawableMaritimeArea => area.geometry != null,
  );
  const scopedAreaIds = new Set(scopedAreas.map((area) => area.id));

  const warningsByAreaId = new Map<string, MaritimeWarning[]>();
  for (const warning of warnings) {
    if (!scopedAreaIds.has(warning.area_id)) {
      continue;
    }
    const list = warningsByAreaId.get(warning.area_id) ?? [];
    list.push(warning);
    warningsByAreaId.set(warning.area_id, list);
  }

  return scopedAreas.map((area) => ({
    area,
    warnings: warningsByAreaId.get(area.id) ?? [],
    geometry: areaToFeature(area),
  }));
}
